import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Download, RefreshCw, Users, CheckCircle, Search, ChevronLeft, ChevronRight, Clock, TrendingUp, UserCog, Wifi, WifiOff, ListTodo, BarChart3, Calendar, FileText, AlertCircle, X, Sparkles, Coffee } from 'lucide-react'
import { AdminService } from '../../services/adminService'
import ConsultationsTable from './ConsultationsTable'
import { useAdminIdentity } from './AdminIdentityContext'
import { formatCooldown, getRemainingCooldownSeconds } from '../../utils/pharmacistCooldown'

const getDefaultLastThreeDaysRange = () => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return {
    from: new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    to: now.toISOString()
  }
}

const AdminDashboard = () => {
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    searchTerm: '',
    dateRange: getDefaultLastThreeDaysRange(),
    filterStatus: 'all'
  })
  // Custom date picker state
  const [customDateFrom, setCustomDateFrom] = useState('')
  const [customDateTo, setCustomDateTo] = useState('')
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [loadingStats, setLoadingStats] = useState(false)
  const [stats, setStats] = useState({
    totalUsers: 0,
    wonduraDone: 0,
    avgProcessingTime: 0,
    conversionRate: 0,
    paidCount: 0
  })
  const requestSequenceRef = useRef(0)
  const statsRequestSequenceRef = useRef(0)
  const lastFilterSignatureRef = useRef('')
  const itemsPerPage = 10

  // Self-status toggle state (for pharmacists)
  const [isOnline, setIsOnline] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [statusFailure, setStatusFailure] = useState(null)
  const [ownPharmacistStatus, setOwnPharmacistStatus] = useState(null)
  const [freelanceLimitModal, setFreelanceLimitModal] = useState(null)
  const [availabilityUnavailable, setAvailabilityUnavailable] = useState(false)
  const [cooldownNow, setCooldownNow] = useState(Date.now)
  const availabilityRequestRef = useRef(0)

  // Freelance UX: waiting state + 10-min snooze
  const [freelanceWaiting, setFreelanceWaiting] = useState(false)
  const [freelanceOnlineStartedAt, setFreelanceOnlineStartedAt] = useState(null)
  const [snoozeUntil, setSnoozeUntil] = useState(null)
  const snoozeTimerRef = useRef(null)

  // Get current user role and permissions
  const verifiedIdentity = useAdminIdentity()
  const currentUser = verifiedIdentity || AdminService.getCurrentUser()
  const userRole = currentUser?.role || null
  const isSuperAdmin = userRole === 'super_admin'
  const isCoordinator = userRole === 'coordinator'
  const isPharmacist = userRole === 'pharmacist'
  const ownPharmacistType = ownPharmacistStatus?.pharmacist_type || 'full_time'
  const ownPharmacistTypeLabel = ownPharmacistType === 'freelance' ? 'Freelance PH' : 'Full-time PH'
  const isFreelance = ownPharmacistType === 'freelance'
  const showTotalConsultations = !isPharmacist || isFreelance
  const remainingFreelanceSlots = Math.max(
    0,
    Number(ownPharmacistStatus?.daily_limit || 0) - Number(ownPharmacistStatus?.daily_counter || 0)
  )
  const cooldownSeconds = getRemainingCooldownSeconds(ownPharmacistStatus, cooldownNow)
  const isCoolingDown = cooldownSeconds > 0
  const capacityBlocked = availabilityUnavailable || cooldownSeconds === null || isCoolingDown
  const hourlyLimitReached = !isFreelance && ownPharmacistStatus &&
    Number(ownPharmacistStatus.hourly_dispatch_count || 0) >= Number(ownPharmacistStatus.hourly_limit || 0)

  // Load dashboard data with filters
  const loadDashboardData = useCallback(async (
    filterParams = filters,
    pageToLoad = currentPage,
    options = {}
  ) => {
    const { includeTotal = true } = options
    const requestId = requestSequenceRef.current + 1
    requestSequenceRef.current = requestId
    setLoading(true)
    setError('')

    try {
      const result = await AdminService.getDashboardData({
        ...filterParams,
        page: pageToLoad,
        pageSize: itemsPerPage,
        includeStats: false,
        includeTotal
      })

      if (requestId !== requestSequenceRef.current) {
        return
      }

      if (result.error) {
        const nextError = result.error?.message || result.error?.details || 'Failed to load dashboard data'
        setError(nextError)
        console.error('Dashboard error:', result.error)
        return
      }

      setDashboardData(result.data || [])
      if (typeof result.total === 'number' && !Number.isNaN(result.total)) {
        setTotalRecords(result.total)
      }
    } catch (error) {
      if (requestId !== requestSequenceRef.current) {
        return
      }

      setError(error?.message || 'An error occurred while loading data')
      console.error('Dashboard load error:', error)
    } finally {
      if (requestId === requestSequenceRef.current) {
        setLoading(false)
      }
    }
  }, [filters, currentPage])

  const loadDashboardStats = useCallback(async (filterParams = filters) => {
    const requestId = statsRequestSequenceRef.current + 1
    statsRequestSequenceRef.current = requestId
    setLoadingStats(true)

    try {
      const result = await AdminService.getDashboardStats(filterParams)

      if (requestId !== statsRequestSequenceRef.current) {
        return
      }

      if (result.error) {
        console.error('Dashboard stats error:', result.error)
        return
      }

      if (!result.stats) {
        return
      }

      setStats({
        totalUsers: result.stats.totalConsultations || 0,
        wonduraDone: result.stats.wonduraDone || 0,
        avgProcessingTime: result.stats.avgProcessingTime || 0,
        conversionRate: result.stats.conversionRate || 0,
        paidCount: result.stats.paidCount || 0
      })
    } catch (error) {
      if (requestId !== statsRequestSequenceRef.current) {
        return
      }
      console.error('Dashboard stats load error:', error)
    } finally {
      if (requestId === statsRequestSequenceRef.current) {
        setLoadingStats(false)
      }
    }
  }, [filters])

  // Set date range preset
  const setDatePreset = useCallback((preset) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    let dateFrom = null
    let dateTo = null

    switch (preset) {
      case '3days':
        dateFrom = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString()
        dateTo = new Date().toISOString()
        break
      case '7days':
        dateFrom = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString()
        dateTo = new Date().toISOString()
        break
      case '1month':
        dateFrom = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString()
        dateTo = new Date().toISOString()
        break
      case '3months':
        dateFrom = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString()
        dateTo = new Date().toISOString()
        break
      case 'all':
        // No date filter
        dateFrom = null
        dateTo = null
        break
      default:
        break
    }

    setFilters(prev => ({
      ...prev,
      dateRange: dateFrom ? { from: dateFrom, to: dateTo } : null
    }))

    // Close custom date picker when using preset
    if (preset !== 'custom') {
      setShowCustomDatePicker(false)
      setCustomDateFrom('')
      setCustomDateTo('')
    }
  }, [])

  // Apply custom date range
  const applyCustomDateRange = useCallback(() => {
    if (customDateFrom && customDateTo) {
      const dateFrom = new Date(customDateFrom).toISOString()
      // Set end of day for the "to" date
      const dateTo = new Date(customDateTo)
      dateTo.setHours(23, 59, 59, 999)

      setFilters(prev => ({
        ...prev,
        dateRange: { from: dateFrom, to: dateTo.toISOString() }
      }))
      setShowCustomDatePicker(false)
    }
  }, [customDateFrom, customDateTo])

  // Check if current date range is custom
  const isCustomDateRange = useCallback(() => {
    if (!filters.dateRange) return false
    const { from } = filters.dateRange
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    // Check if it's not any of the presets
    const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000)
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
    const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
    const threeMonthsAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)

    const fromDate = new Date(from)

    return (
      fromDate.getTime() !== threeDaysAgo.getTime() &&
      fromDate.getTime() !== sevenDaysAgo.getTime() &&
      fromDate.getTime() !== oneMonthAgo.getTime() &&
      fromDate.getTime() !== threeMonthsAgo.getTime()
    )
  }, [filters.dateRange])

  // Clear date range
  const clearDateRange = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      dateRange: null
    }))
  }, [])

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage))
  const startIndex = totalRecords === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1
  const endIndex = totalRecords === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalRecords)

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // Handle logout
  const handleLogout = async () => {
    await AdminService.logout()
    window.location.reload()
  }

  // Export data
  const handleExport = async () => {
    setError('')

    try {
      const result = await AdminService.getDashboardData({
        ...filters,
        fetchAll: true,
        includeStats: false
      })

      if (result.error) {
        setError(result.error?.message || 'Failed to export dashboard data')
        return
      }

      AdminService.exportToCSV(result.data || [])
    } catch (error) {
      setError(error?.message || 'Failed to export dashboard data')
    }
  }

  const handleRefresh = async () => {
    refreshOwnStatus()
    await loadDashboardData(filters, currentPage, { includeTotal: true })
    loadDashboardStats(filters)
  }

  // Debounced search term to avoid excessive re-renders/fetches
  const [searchQuery, setSearchQuery] = useState(filters.searchTerm)

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => {
        if (prev.searchTerm === searchQuery) {
          return prev
        }

        return { ...prev, searchTerm: searchQuery }
      })
    }, 500) // 500ms debounce
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Load data on component mount, filter change, and page change
  useEffect(() => {
    const filterSignature = JSON.stringify({
      searchTerm: filters.searchTerm || '',
      dateFrom: filters.dateRange?.from || null,
      dateTo: filters.dateRange?.to || null,
      filterStatus: filters.filterStatus || 'all'
    })

    const filtersChanged = lastFilterSignatureRef.current !== filterSignature

    if (filtersChanged) {
      lastFilterSignatureRef.current = filterSignature
      if (currentPage !== 1) {
        setCurrentPage(1)
        return
      }
    }

    loadDashboardData(filters, currentPage, { includeTotal: filtersChanged || currentPage === 1 })
  }, [loadDashboardData, filters, currentPage])

  // Load KPI stats asynchronously after filter changes (not on page navigation)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadDashboardStats(filters)
    }, 150)

    return () => clearTimeout(timer)
  }, [loadDashboardStats, filters])

  // Load own online status for pharmacists
  const refreshOwnStatus = useCallback(async () => {
    if (!isPharmacist) return null
    const requestId = ++availabilityRequestRef.current
    try {
      const result = await AdminService.getOwnOnlineStatus()
      if (requestId !== availabilityRequestRef.current) return null
      if (result?.success) {
        setIsOnline(result.is_online)
        setOwnPharmacistStatus(result)
        setAvailabilityUnavailable(false)
        setCooldownNow(Date.now())
        return result
      }
    } catch (_error) {
      // Keep the last known online state so going offline remains available.
    }
    if (requestId === availabilityRequestRef.current) setAvailabilityUnavailable(true)
    return null
  }, [isPharmacist])

  useEffect(() => {
    refreshOwnStatus()
    return () => { availabilityRequestRef.current += 1 }
  }, [refreshOwnStatus])

  // Count down from the persisted assignment time, never from page load.
  useEffect(() => {
    setCooldownNow(Date.now())
    if (!isPharmacist || !(getRemainingCooldownSeconds(ownPharmacistStatus) > 0)) return undefined
    const intervalId = setInterval(() => {
      const now = Date.now()
      setCooldownNow(now)
      if (getRemainingCooldownSeconds(ownPharmacistStatus, now) === 0) clearInterval(intervalId)
    }, 1000)
    return () => clearInterval(intervalId)
  }, [isPharmacist, ownPharmacistStatus])

  // Tracks the daily_limit value at which the user dismissed the "more" modal,
  // so polling doesn't re-open it until the limit advances.
  const moreModalDismissedAtLimitRef = useRef(null)

  // Both types can receive assignments or availability changes outside this tab.
  useEffect(() => {
    if (!isPharmacist) return undefined
    let refreshing = false
    const tick = async () => {
      if (document.visibilityState === 'hidden' || refreshing) return
      refreshing = true
      try {
        await refreshOwnStatus()
      } finally {
        refreshing = false
      }
    }
    const intervalId = setInterval(tick, 10000)
    window.addEventListener('focus', tick)
    document.addEventListener('visibilitychange', tick)
    return () => {
      clearInterval(intervalId)
      window.removeEventListener('focus', tick)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [isPharmacist, refreshOwnStatus])

  useEffect(() => {
    if (!isPharmacist || !isFreelance || !isOnline || !ownPharmacistStatus) return
    const counter = Number(ownPharmacistStatus.daily_counter || 0)
    const limit = Number(ownPharmacistStatus.daily_limit || 0)
    if (freelanceOnlineStartedAt && counter > Number(freelanceOnlineStartedAt.startingCounter || 0)) {
      setFreelanceWaiting(false)
    }

    // Offer more only once the chosen allowance AND the cooldown have finished.
    if (counter >= limit && limit > 0 && !capacityBlocked) {
      setFreelanceLimitModal(prev => {
        if (prev || moreModalDismissedAtLimitRef.current === limit) return prev
        return { mode: 'more' }
      })
    } else if (counter < limit) {
      moreModalDismissedAtLimitRef.current = null
    }
  }, [isPharmacist, isFreelance, isOnline, ownPharmacistStatus, freelanceOnlineStartedAt, capacityBlocked])

  // Restore snooze from localStorage on mount and arm the resume modal.
  useEffect(() => {
    if (!isPharmacist || !isFreelance) return undefined
    try {
      const stored = window.localStorage.getItem('wondura_freelance_snooze_until')
      const until = stored ? Number(stored) : 0
      if (!until) return undefined

      if (until <= Date.now()) {
        // Snooze already expired before mount → show the resume prompt and clear.
        window.localStorage.removeItem('wondura_freelance_snooze_until')
        setSnoozeUntil(null)
        setFreelanceLimitModal({ mode: 'resume' })
        return undefined
      }

      setSnoozeUntil(until)
      const ms = until - Date.now()
      snoozeTimerRef.current = setTimeout(() => {
        window.localStorage.removeItem('wondura_freelance_snooze_until')
        setSnoozeUntil(null)
        setFreelanceLimitModal({ mode: 'resume' })
      }, ms)
      return () => {
        if (snoozeTimerRef.current) {
          clearTimeout(snoozeTimerRef.current)
          snoozeTimerRef.current = null
        }
      }
    } catch (_err) {
      return undefined
    }
  }, [isPharmacist, isFreelance])

  const dismissFreelanceModal = () => {
    if (freelanceLimitModal?.mode === 'more') {
      moreModalDismissedAtLimitRef.current = Number(ownPharmacistStatus?.daily_limit || 0)
    }
    setFreelanceLimitModal(null)
  }

  const handleFreelanceGoOnline = async (additionalLimit, mode = 'initial') => {
    if (capacityBlocked) return
    const startingCounter = Number(ownPharmacistStatus?.daily_counter || 0)
    const result = await handleToggleOwnStatus(true, { additionalLimit, mode })
    if (result?.success) {
      setFreelanceOnlineStartedAt({ at: new Date().toISOString(), startingCounter })
      setFreelanceWaiting(true)
      moreModalDismissedAtLimitRef.current = null
      setFreelanceLimitModal(null)
    }
  }

  const handleFreelanceSnooze = async () => {
    setFreelanceLimitModal(null)
    await handleToggleOwnStatus(false)
    const until = Date.now() + 10 * 60 * 1000
    try {
      window.localStorage.setItem('wondura_freelance_snooze_until', String(until))
    } catch (_err) {
      // localStorage unavailable — chip will still render from state until reload
    }
    setSnoozeUntil(until)
    if (snoozeTimerRef.current) clearTimeout(snoozeTimerRef.current)
    snoozeTimerRef.current = setTimeout(() => {
      try {
        window.localStorage.removeItem('wondura_freelance_snooze_until')
      } catch (_err) { /* noop */ }
      setSnoozeUntil(null)
      setFreelanceLimitModal({ mode: 'resume' })
    }, 10 * 60 * 1000)
  }

  const handleFreelanceGoOfflineFromModal = async () => {
    setFreelanceLimitModal(null)
    await handleToggleOwnStatus(false)
    // Manual offline cancels any pending snooze.
    if (snoozeTimerRef.current) {
      clearTimeout(snoozeTimerRef.current)
      snoozeTimerRef.current = null
    }
    try {
      window.localStorage.removeItem('wondura_freelance_snooze_until')
    } catch (_err) { /* noop */ }
    setSnoozeUntil(null)
  }

  // Toggle own online status (for pharmacists)
  const handleToggleOwnStatus = async (desiredStatus = null, options = {}) => {
    if (togglingStatus) return { success: false, reason: 'busy' }

    const newStatus = typeof desiredStatus === 'boolean' ? desiredStatus : !isOnline
    if (newStatus && ownPharmacistStatus?.pharmacist_type === 'freelance' && !options.additionalLimit) {
      setFreelanceLimitModal({ mode: isOnline ? 'more' : 'initial' })
      return { success: false, reason: 'needs_capacity' }
    }

    setTogglingStatus(true)
    setStatusFailure(null)
    const previousStatus = isOnline

    try {
      if (newStatus && isFreelance) {
        // A new assignment may have arrived while the capacity dialog was open.
        // This is a UI guard, not a substitute for atomic dispatcher enforcement.
        const fresh = await refreshOwnStatus()
        if (!fresh || getRemainingCooldownSeconds(fresh) !== 0) {
          return { success: false, reason: 'cooldown_or_status_unavailable' }
        }
      }
      setIsOnline(newStatus)
      const result = await AdminService.toggleOwnOnlineStatus(newStatus, options)

      if (result.success && result.verified && result.is_online === newStatus) {
        setIsOnline(result.is_online)
        await refreshOwnStatus()
        if (!newStatus) {
          // Going offline cancels any waiting/snapshot UI state.
          setFreelanceWaiting(false)
          setFreelanceOnlineStartedAt(null)
        }
        return { success: true }
      }

      const latestStatus = await refreshOwnStatus()
      const restoredStatus = latestStatus ? latestStatus.is_online : previousStatus
      setIsOnline(restoredStatus)
      setStatusFailure({
        attemptedStatus: newStatus,
        currentStatus: restoredStatus,
        message: result.error || 'Status update could not be verified in Supabase.',
      })
      return { success: false, reason: 'verification_failed', error: result.error }
    } catch (err) {
      const latestStatus = await refreshOwnStatus()
      const restoredStatus = latestStatus ? latestStatus.is_online : previousStatus
      setIsOnline(restoredStatus)
      setStatusFailure({
        attemptedStatus: newStatus,
        currentStatus: restoredStatus,
        message: err?.message || 'Connection lost while updating your online status.',
      })
      return { success: false, reason: 'exception', error: err?.message }
    } finally {
      setTogglingStatus(false)
    }
  }

  const availabilityLabel = availabilityUnavailable || (ownPharmacistStatus && cooldownSeconds === null)
    ? 'Availability unavailable'
    : !ownPharmacistStatus ? 'Checking availability…'
      : isCoolingDown ? 'Next consultation in'
        : !isOnline ? 'Offline — not receiving'
          : isFreelance && remainingFreelanceSlots === 0 ? 'Allowance reached'
            : hourlyLimitReached ? 'Hourly limit reached'
              : 'Cooldown complete'
  const capacityNotice = capacityBlocked && (
    <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900" dir="rtl">
      {isCoolingDown && !availabilityUnavailable
        ? <>يمكنك طلب استشارات إضافية بعد <span dir="ltr" className="font-semibold tabular-nums">{formatCooldown(cooldownSeconds)}</span>. يمكنك إيقاف الاتصال في أي وقت.</>
        : 'تعذّر تأكيد الجاهزية الآن. انتظري تحديث الحالة قبل طلب استشارات إضافية.'}
    </p>
  )

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {freelanceLimitModal?.mode === 'initial' && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="freelance-initial-title"
          dir="rtl"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-sm"
        >
          <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-[0_30px_80px_-20px_rgba(167,58,93,0.35)] ring-1 ring-rose-100/70">
            <div className="relative bg-gradient-to-br from-[#A73A5D] via-[#c44b78] to-[#d86b8d] px-7 pt-7 pb-8 text-white">
              <div className="absolute -top-10 -left-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" aria-hidden />
              <div className="absolute -bottom-12 -right-12 h-44 w-44 rounded-full bg-rose-200/20 blur-3xl" aria-hidden />
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-medium tracking-wide ring-1 ring-white/25">
                <Sparkles className="h-3.5 w-3.5" />
                استشارات 
              </span>
              <h3 id="freelance-initial-title" className="mt-3 text-2xl font-bold leading-tight">
                كم استشارة حابه تأخدي؟
              </h3>
              <p className="mt-2 text-sm leading-6 text-rose-50/90">
                اختاري عدد الاستشارات الجديدة اللي حابه تستقبليها الآن.
                {' '}يتم توزيعها بفاصل ٣ دقائق على الأقل بين كل استشارة والتالية.
              </p>
            </div>
            <div className="p-6">
              {capacityNotice}
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleFreelanceGoOnline(amount, 'initial')}
                    disabled={togglingStatus || capacityBlocked}
                    className="group relative aspect-[5/4] overflow-hidden rounded-2xl border border-rose-100 bg-gradient-to-br from-white to-rose-50/60 px-4 py-4 text-center transition-all duration-200 hover:-translate-y-0.5 hover:border-[#A73A5D]/40 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#A73A5D]/40 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="block text-[11px] font-medium tracking-wide text-slate-400">استشارة</span>
                    <span className="mt-1 block text-4xl font-black text-[#A73A5D]">{amount}</span>
                    <span className="mt-1 block text-[11px] text-slate-400 group-hover:text-[#A73A5D]/70">الآن</span>
                    <span className="pointer-events-none absolute inset-x-4 bottom-2 h-px scale-x-0 bg-gradient-to-r from-transparent via-[#A73A5D]/30 to-transparent transition-transform duration-300 group-hover:scale-x-100" aria-hidden />
                  </button>
                ))}
              </div>
              <button
                onClick={dismissFreelanceModal}
                disabled={togglingStatus}
                className="mt-5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {freelanceLimitModal?.mode === 'more' && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="freelance-more-title"
          dir="rtl"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-sm"
        >
          <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white ring-1 ring-emerald-100/70 shadow-[0_30px_80px_-20px_rgba(16,185,129,0.30)]">
            <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-[#A73A5D] px-7 pt-7 pb-8 text-white">
              <div className="absolute -top-10 -left-10 h-40 w-40 rounded-full bg-white/15 blur-2xl" aria-hidden />
              <div className="absolute -bottom-12 -right-12 h-44 w-44 rounded-full bg-emerald-200/20 blur-3xl" aria-hidden />
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-medium ring-1 ring-white/25">
                <CheckCircle className="h-3.5 w-3.5" /> أنجزتي العدد المختار
              </span>
              <h3 id="freelance-more-title" className="mt-3 text-2xl font-bold leading-tight">
                حابه تاخدي استشارات كمان؟
              </h3>
              <p className="mt-2 text-sm leading-6 text-emerald-50/90">
                وصلتي للعدد اللي اخترتيه. إذا جاهزة، اختاري عدد إضافي أو خدي استراحة قصيرة.
              </p>
            </div>
            <div className="p-6">
              {capacityNotice}
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleFreelanceGoOnline(amount, 'more')}
                    disabled={togglingStatus || capacityBlocked}
                    className="group aspect-square rounded-2xl border border-rose-100 bg-gradient-to-br from-white to-rose-50/60 px-3 py-3 text-center transition-all duration-200 hover:-translate-y-0.5 hover:border-[#A73A5D]/40 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#A73A5D]/40 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="block text-3xl font-black text-[#A73A5D]">{amount}</span>
                    <span className="mt-0.5 block text-[11px] text-slate-400 group-hover:text-[#A73A5D]/70">إضافي</span>
                  </button>
                ))}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  onClick={handleFreelanceSnooze}
                  disabled={togglingStatus}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-60"
                >
                  <Coffee className="h-4 w-4" /> تأجيل ١٠ دقائق
                </button>
                <button
                  onClick={handleFreelanceGoOfflineFromModal}
                  disabled={togglingStatus}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-60"
                >
                  <WifiOff className="h-4 w-4" /> إيقاف الاتصال
                </button>
              </div>

              <button
                onClick={dismissFreelanceModal}
                className="mt-4 w-full rounded-xl px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      {freelanceLimitModal?.mode === 'resume' && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="freelance-resume-title"
          dir="rtl"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-sm"
        >
          <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white ring-1 ring-amber-100/70 shadow-[0_30px_80px_-20px_rgba(245,158,11,0.30)]">
            <div className="relative bg-gradient-to-br from-amber-400 via-rose-300 to-[#A73A5D] px-7 pt-7 pb-8 text-white">
              <div className="absolute -top-10 -left-10 h-40 w-40 rounded-full bg-white/15 blur-2xl" aria-hidden />
              <div className="absolute -bottom-12 -right-12 h-44 w-44 rounded-full bg-amber-200/20 blur-3xl" aria-hidden />
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-medium ring-1 ring-white/25">
                <Coffee className="h-3.5 w-3.5" /> انتهت الاستراحة
              </span>
              <h3 id="freelance-resume-title" className="mt-3 text-2xl font-bold leading-tight">
                انتهت الاستراحة، حابه ترجعي تستقبلي استشارات؟
              </h3>
              <p className="mt-2 text-sm leading-6 text-rose-50/95">
                اختاري العدد اللي تحبيه أو ابقي أوفلاين شوي بعد.
              </p>
            </div>
            <div className="p-6">
              {capacityNotice}
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleFreelanceGoOnline(amount, 'initial')}
                    disabled={togglingStatus || capacityBlocked}
                    className="group relative aspect-[5/4] overflow-hidden rounded-2xl border border-rose-100 bg-gradient-to-br from-white to-rose-50/60 px-4 py-4 text-center transition-all duration-200 hover:-translate-y-0.5 hover:border-[#A73A5D]/40 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#A73A5D]/40 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="block text-[11px] font-medium tracking-wide text-slate-400">استشارة</span>
                    <span className="mt-1 block text-4xl font-black text-[#A73A5D]">{amount}</span>
                    <span className="mt-1 block text-[11px] text-slate-400 group-hover:text-[#A73A5D]/70">الآن</span>
                  </button>
                ))}
              </div>
              <button
                onClick={dismissFreelanceModal}
                className="mt-5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                أبقى أوفلاين
              </button>
            </div>
          </div>
        </div>
      )}

      {statusFailure && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-md rounded-2xl border border-red-100 bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-red-100 p-2 text-red-600">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-semibold text-slate-900">Availability update failed</h3>
                  <button
                    onClick={() => setStatusFailure(null)}
                    className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Close availability error"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  We could not confirm that Supabase saved you as{' '}
                  <span className="font-semibold text-slate-900">
                    {statusFailure.attemptedStatus ? 'online' : 'offline'}
                  </span>
                  .
                </p>
                <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {statusFailure.message}
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Current confirmed UI status:{' '}
                  <span className="font-semibold">
                    {statusFailure.currentStatus ? 'Online' : 'Offline'}
                  </span>
                </p>
                <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button
                    onClick={() => setStatusFailure(null)}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleToggleOwnStatus(statusFailure.attemptedStatus)}
                    disabled={togglingStatus}
                    className="rounded-lg bg-[#A73A5D] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#8f2f4f] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {togglingStatus ? 'Trying again...' : 'Try again'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-4 py-4">
            <div className="flex flex-wrap items-center gap-y-2">
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <span className="ml-3 px-2 py-1 bg-[#A73A5D]/10 text-[#A73A5D] text-xs rounded-full">
                Wonder Beauties
              </span>
              <span className={`ml-2 px-2 py-1 text-xs rounded-full ${isSuperAdmin
                ? 'bg-purple-100 text-purple-800'
                : isCoordinator
                  ? 'bg-teal-100 text-teal-800'
                  : 'bg-blue-100 text-blue-800'
                }`}>
                {isSuperAdmin ? 'Super Admin' : isCoordinator ? 'Coordinator' : isPharmacist ? 'Pharmacist' : 'Verifying account'}
              </span>
              {isPharmacist && (
                <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${ownPharmacistType === 'freelance'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-sky-100 text-sky-800'
                  }`}>
                  {ownPharmacistTypeLabel}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* User Management Button - Super Admin only */}
              {isSuperAdmin && (
                <button
                  onClick={() => navigate('/admin/users')}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Manage Users
                </button>
              )}

              {/* Pharmacist Management - Super Admin and Coordinator */}
              {(isSuperAdmin || isCoordinator) && (
                <button
                  onClick={() => navigate('/admin/pharmacists')}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Manage Pharmacists
                </button>
              )}

              {/* Pending Queue - Super Admin and Coordinator */}
              {(isSuperAdmin || isCoordinator) && (
                <button
                  onClick={() => navigate('/admin/pending')}
                  className="flex items-center px-3 py-2 text-sm text-orange-600 hover:text-orange-900 hover:bg-orange-50 rounded-md transition-colors"
                >
                  <ListTodo className="w-4 h-4 mr-2" />
                  Pending Queue
                </button>
              )}

              {/* Assignment Logs - Super Admin and Coordinator */}
              {(isSuperAdmin || isCoordinator) && (
                <button
                  onClick={() => navigate('/admin/assignment-logs')}
                  className="flex items-center px-3 py-2 text-sm text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-md transition-colors"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Assignment Logs
                </button>
              )}

              {/* Daily Reports - Super Admin and Coordinator */}
              {(isSuperAdmin || isCoordinator) && (
                <button
                  onClick={() => navigate('/admin/reports')}
                  className="flex items-center px-3 py-2 text-sm text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded-md transition-colors"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Daily Reports
                </button>
              )}


              {/* Online/Offline Toggle - Pharmacists only */}
              {isPharmacist && (
                <div className="flex flex-wrap items-center gap-2">
                  <div
                    aria-label="Consultation availability"
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${capacityBlocked
                      ? 'border-amber-200 bg-amber-50 text-amber-900'
                      : 'border-slate-200 bg-slate-50 text-slate-700'}`}
                    title="Cooldown is measured from the last assignment. Receiving another consultation also requires available capacity and online status."
                  >
                    <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <div>
                      <div className="text-xs font-semibold">{availabilityLabel}</div>
                      <div className="mt-0.5 text-[11px] opacity-75">
                        {isFreelance ? '3 min' : '5 min'} between assignments
                      </div>
                    </div>
                    {isCoolingDown && !availabilityUnavailable && (
                      <span role="timer" aria-label="Cooldown remaining" aria-live="off" dir="ltr" className="text-xl font-semibold tabular-nums tracking-tight">
                        {formatCooldown(cooldownSeconds)}
                      </span>
                    )}
                  </div>
                  {isFreelance && isOnline && freelanceWaiting && remainingFreelanceSlots > 0 && !capacityBlocked && (
                    <span
                      className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200"
                      dir="rtl"
                      title="بانتظار استشارة جديدة"
                    >
                      <span className="relative inline-flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
                      </span>
                      بانتظار استشارة جديدة
                    </span>
                  )}
                  {snoozeUntil && Date.now() < snoozeUntil && (
                    <span
                      className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200"
                      dir="rtl"
                    >
                      <Coffee className="h-3.5 w-3.5" />
                      استراحة حتى {new Date(snoozeUntil).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {isFreelance && isOnline && remainingFreelanceSlots === 0 && (
                    <button
                      aria-label="Request more consultations"
                      onClick={() => setFreelanceLimitModal({ mode: 'more' })}
                      disabled={togglingStatus || capacityBlocked}
                      className="rounded-lg border border-[#A73A5D]/30 px-3 py-2 text-sm font-medium text-[#A73A5D] transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Request more
                    </button>
                  )}
                  <button
                    aria-label={isOnline ? 'Go offline' : 'Go online'}
                    onClick={() => handleToggleOwnStatus()}
                    disabled={togglingStatus || (!isOnline && (!ownPharmacistStatus || availabilityUnavailable))}
                    className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-all ${isOnline
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${togglingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {togglingStatus ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : isOnline ? (
                      <Wifi className="w-4 h-4 mr-2" />
                    ) : (
                      <WifiOff className="w-4 h-4 mr-2" />
                    )}
                    {togglingStatus ? 'Updating...' : isOnline ? 'Online' : 'Offline'}
                    {ownPharmacistStatus?.pharmacist_type === 'freelance' && (
                      <span className="ml-2 text-xs opacity-80">
                        {isOnline ? `${remainingFreelanceSlots} left` : 'Freelance'}
                      </span>
                    )}
                  </button>
                </div>
              )}

              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading || loadingStats ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              {/* Export CSV button - Super Admin only */}
              {isSuperAdmin && (
                <button
                  onClick={handleExport}
                  className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 text-sm text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* KPI Cards */}
        <div className={`grid grid-cols-1 ${showTotalConsultations ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-6 mb-8`}>
          {showTotalConsultations && (
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Consultations</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalUsers}</p>
                  {loadingStats && <p className="text-xs text-gray-500 mt-1">Updating...</p>}
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Ready to Process</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.wonduraDone}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Processing Time</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.avgProcessingTime} min</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.conversionRate}%</p>
                <p className="text-xs text-gray-500 mt-1">{stats.paidCount} paid</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col gap-4">
            {/* Search */}
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by phone or user ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4 items-center">
              {/* Date Range Presets */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Date:</span>
                <button
                  onClick={() => setDatePreset('all')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${!filters.dateRange
                    ? 'bg-[#A73A5D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  All Time
                </button>
                <button
                  onClick={() => setDatePreset('3days')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${filters.dateRange?.from &&
                    new Date(filters.dateRange.from).getTime() >= new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).getTime()
                    ? 'bg-[#A73A5D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Last 3 Days
                </button>
                <button
                  onClick={() => setDatePreset('7days')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${filters.dateRange?.from &&
                    new Date(filters.dateRange.from).getTime() >= new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).getTime() &&
                    new Date(filters.dateRange.from).getTime() < new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).getTime()
                    ? 'bg-[#A73A5D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Last 7 Days
                </button>
                <button
                  onClick={() => setDatePreset('1month')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${filters.dateRange?.from &&
                    new Date(filters.dateRange.from).getTime() >= new Date(Date.now() - 31 * 24 * 60 * 60 * 1000).getTime() &&
                    new Date(filters.dateRange.from).getTime() < new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).getTime()
                    ? 'bg-[#A73A5D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Last Month
                </button>
                <button
                  onClick={() => setDatePreset('3months')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${filters.dateRange?.from &&
                    new Date(filters.dateRange.from).getTime() >= new Date(Date.now() - 91 * 24 * 60 * 60 * 1000).getTime() &&
                    new Date(filters.dateRange.from).getTime() < new Date(Date.now() - 31 * 24 * 60 * 60 * 1000).getTime()
                    ? 'bg-[#A73A5D] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Last 3 Months
                </button>

                {/* Custom Date Picker Toggle */}
                <div className="relative">
                  <button
                    onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${showCustomDatePicker || (isCustomDateRange() && !filters.dateRange?.from?.includes('T'))
                      ? 'bg-[#A73A5D] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    Custom Range
                  </button>

                  {/* Custom Date Picker Dropdown */}
                  {showCustomDatePicker && (
                    <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 w-72">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">From</label>
                          <input
                            type="date"
                            value={customDateFrom}
                            onChange={(e) => setCustomDateFrom(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">To</label>
                          <input
                            type="date"
                            value={customDateTo}
                            onChange={(e) => setCustomDateTo(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent"
                          />
                        </div>
                        <button
                          onClick={applyCustomDateRange}
                          disabled={!customDateFrom || !customDateTo}
                          className="w-full px-4 py-2 bg-[#A73A5D] text-white rounded-md hover:bg-[#8C2E4A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Clear Date Filter */}
                {filters.dateRange && (
                  <button
                    onClick={clearDateRange}
                    className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Status Filter */}
              <div className="sm:w-48">
                <select
                  value={filters.filterStatus}
                  onChange={(e) => handleFilterChange('filterStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="wondura_done">Ready (Wondura Done)</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing {startIndex}-{endIndex} of {totalRecords} records
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>

                <div className="flex items-center space-x-1">
                  {/* Show page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-md ${currentPage === pageNum
                          ? 'bg-[#A73A5D] text-white border-[#A73A5D]'
                          : 'border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => loadDashboardData(filters, currentPage, { includeTotal: true })}
              className="mt-2 text-red-600 hover:text-red-800 underline text-sm"
            >
              Try again
            </button>
          </div>
        )}

        {/* Consultations Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <ConsultationsTable
            data={dashboardData}
            loading={loading}
            onRefresh={() => loadDashboardData(filters, currentPage, { includeTotal: false })}
            userRole={userRole}
            currentUsername={currentUser?.username}
          />
        </div>
      </main>
    </div>
  )
}

export default AdminDashboard
